// @ts-strict-ignore
import {ReportName, ResourceType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {apiClient} from 'common/util/api-client';
import {initMixpanel} from 'common/report/tracking-setup';
import {warnDeveloper} from 'common/report/util';
import {getLocation} from 'common/util/url';

// TODO: Move this file to common/report/util/

let mixpanel = null;

function getMixpanelInstance() {
  if (!mixpanel) {
    // Defer initMixpanel until we need the mixpanel instance. This is to fix wct test where this
    // module is loaded before the global mixpanel has been stubbed.
    mixpanel = initMixpanel(PROJECT_3_MIXPANEL_TOKEN);
  }
  return mixpanel;
}

const REPORT_NAME_SET = new Set(Object.values(ReportName) as string[]);

function getTimestamp() {
  return Math.round(new Date().getTime() / 1000);
}

// We generate a random id that's used for both client-side and server-side tracking.
// This allows (manually) linking the associated the client-side and server-side tracking if we
// want to do more in-depth analysis.
function generateRandomId() {
  // Uses the same logic as for generating $insert_id in the mixpanel-js SDK:
  // https://github.com/mixpanel/mixpanel-js/blob/bf905cfcda6fa1f8c29edfb857d7e28932a4fd4d/src/utils.js#L1648
  return Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
}

export enum ActionType {
  Filter = `filter`,
  Breakdown = `breakdown`,
}

export function trackLearningAction(params: {
  projectId: number | string;
  reportName: string;
  actionType: ActionType;
  resourceType: ResourceType;
}) {
  const {projectId: rawProjectId, reportName, actionType, resourceType} = params;
  const projectId = Number(rawProjectId);

  // Catch potential misnamed/missing report name
  if (!REPORT_NAME_SET.has(reportName)) {
    warnDeveloper(`trackLearningAction: Invalid report name`, {reportName});
    return;
  }

  const essentialProps = {
    project_id: projectId,
    report_name: reportName,
    time: getTimestamp(),
    track_id: generateRandomId(),
  };
  const extraProps = {
    action_type: actionType,
    resource_type: resourceType,
  };
  apiClient.post(`/api/app/me/learning-action`, {...essentialProps, tracking_props: extraProps});
  getMixpanelInstance().track(`Learning Action`, {...essentialProps, ...extraProps});
}

export enum VisitedReportReason {
  /** Selecting a bookmark from the bookmark picker */
  /** Server loading via old codepath */
  /** Server loading report via volt codepath */
  ReportVoltLoad = `report_volt_load`,
  /** Navigating between pages (in volt mode) */
  ReportVoltNavigate = `report_volt_navigate`,
  /** Opening a report modal from dashboards */
  ReportModal = `report_modal`,
}

export function trackVisitedReport(params: {
  projectId: number | string;
  reportName: string;
  reason: VisitedReportReason;
  selectedBookmark?: boolean;
  additionalProps?: Obj;
}) {
  const {projectId: rawProjectId, reportName, selectedBookmark = false, reason, additionalProps} = params;
  const projectId = Number(rawProjectId);

  // Catch potential misnamed/missing report name
  if (!REPORT_NAME_SET.has(reportName)) {
    reporter.error(`trackVisitedReport: Invalid report name`, {reportName});
    return;
  }

  const queryParams = new URLSearchParams(getLocation().search);
  const mpSource = queryParams.get(`mp_source`);

  const essentialProps = {
    project_id: projectId,
    report_name: reportName,
    time: getTimestamp(),
    track_id: generateRandomId(),
  };
  const extraProps = {
    ...(additionalProps || {}),
    selected_bookmark: selectedBookmark,
    reason,
    'arrived from (mp_source)': mpSource,
  };

  apiClient.post(`/api/app/me/visited-report`, {...essentialProps, tracking_props: extraProps});
  getMixpanelInstance().track(`Visited Report`, {...essentialProps, ...extraProps});
}
